/**
 * Followers: fans who follow you (ISSUE-1372). Each row has a Message
 * shortcut and Remove, which asks first through the shared confirm, naming
 * the fan. The removed fan is not told.
 */
import {
  useFanFollowFollowersList,
  useFanFollowRemoveFollower,
} from "@sportsmet/profile-sdk/private";
import { Button, confirmDestructive } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  FOLLOWERS_EMPTY_BODY,
  FOLLOWERS_EMPTY_HEADLINE,
  MESSAGE_LABEL,
  messageLabelFor,
  REMOVE_FOLLOWER_CONFIRM_BODY,
  REMOVE_FOLLOWER_CONFIRM_LABEL,
  REMOVE_LABEL,
  removeFollowerConfirmTitle,
} from "./followCopy";
import { followsYouSinceLine } from "./followDates";
import { FOLLOW_LIST_CLASS, FollowListFrame } from "./FollowListFrame";
import {
  canShowMore,
  FOLLOW_LIST_PAGE_SIZE,
  nextFollowListLimit,
} from "./followListPaging";
import { FollowPersonRow } from "./FollowPersonRow";
import { useShowListError } from "./useFollowListQueryErrors";
import { useMessageShortcut } from "./useMessageShortcut";

export const FollowersTab = (): ReactNode => {
  const [limit, setLimit] = useState(FOLLOW_LIST_PAGE_SIZE);
  const list = useFanFollowFollowersList({ limit });
  const remove = useFanFollowRemoveFollower();
  const shortcut = useMessageShortcut();
  useShowListError(list);

  const items = list.data?.data?.items ?? [];
  const removingAccountId = remove.isPending
    ? remove.variables.accountId
    : null;

  async function handleRemove(
    accountId: string,
    displayName: string,
  ): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: removeFollowerConfirmTitle(displayName),
      body: REMOVE_FOLLOWER_CONFIRM_BODY,
      confirmLabel: REMOVE_FOLLOWER_CONFIRM_LABEL,
    });
    if (!isConfirmed) return;
    try {
      showApiError(await remove.mutateAsync({ accountId }));
    } catch {
      showOfflineError();
    }
  }

  return (
    <FollowListFrame
      isLoading={list.isPending}
      isEmpty={items.length === 0}
      emptyHeadline={FOLLOWERS_EMPTY_HEADLINE}
      emptyBody={FOLLOWERS_EMPTY_BODY}
      canShowMore={canShowMore({
        limit,
        nextCursor: list.data?.data?.nextCursor,
      })}
      onShowMore={() => {
        setLimit(nextFollowListLimit);
      }}
      isShowingMore={list.isFetching}
      testId="follow-followers"
    >
      <ul className={FOLLOW_LIST_CLASS}>
        {items.map((item) => (
          <FollowPersonRow
            key={item.accountId}
            person={item}
            caption={followsYouSinceLine(item.followedAt)}
            testId={`follow-follower-${item.accountId}`}
            actions={
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={messageLabelFor(item.displayName)}
                  loading={shortcut.openingAccountId === item.accountId}
                  loadingLabel="Opening chat"
                  onClick={() => {
                    shortcut.message(item.accountId);
                  }}
                  data-testid={`follow-follower-message-${item.accountId}`}
                >
                  {MESSAGE_LABEL}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={removingAccountId === item.accountId}
                  loadingLabel="Removing"
                  onClick={() => {
                    void handleRemove(item.accountId, item.displayName);
                  }}
                  data-testid={`follow-follower-remove-${item.accountId}`}
                >
                  {REMOVE_LABEL}
                </Button>
              </>
            }
          />
        ))}
      </ul>
    </FollowListFrame>
  );
};
